'use client';

import React, { useEffect, useState, useRef } from 'react';
import Link from 'next/link';
import { toast } from 'sonner';
import InviteFriends from '@/components/InviteFriends';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/local-api/client';
import { BellIcon, ShieldCheckIcon, QuestionMarkCircleIcon, ChevronRightIcon, StarIcon, TrophyIcon, BookmarkIcon, PencilSquareIcon, UserGroupIcon, CheckBadgeIcon, PencilIcon, ArrowRightOnRectangleIcon, ChatBubbleLeftIcon, HeartIcon, CameraIcon, XMarkIcon, CheckIcon, EyeIcon, LockClosedIcon, GlobeAltIcon, ExclamationCircleIcon,  } from '@heroicons/react/24/outline';

const badges = [
  { id: 'b-1', label: 'Reviewer', bgColor: 'bg-blue-500/20', iconColor: 'text-blue-400', icon: StarIcon },
  { id: 'b-2', label: 'Top Contributor', bgColor: 'bg-yellow-500/20', iconColor: 'text-yellow-400', icon: TrophyIcon },
  { id: 'b-3', label: 'Early Adopter', bgColor: 'bg-orange-500/20', iconColor: 'text-orange-400', icon: CheckBadgeIcon },
  { id: 'b-4', label: 'Community', bgColor: 'bg-green-500/20', iconColor: 'text-green-400', icon: UserGroupIcon },
];

interface UserProfile {
  full_name: string;
  email: string;
  username?: string;
  bio?: string;
  avatar_url?: string;
  cover_url?: string;
  posts_count?: number;
  following_count?: number;
  followers_count?: number;
  created_at?: string;
}

interface ActivityPost {
  id: string;
  content: string;
  tag: string;
  likes: number;
  comments: number;
  created_at: string;
}

interface SavedPost {
  id: string;
  post_id: string;
  created_at: string;
  community_posts?: {
    content: string;
    tag: string;
    likes: number;
  };
}

interface SavedGroupPost {
  id: string;
  post_id: string;
  created_at: string;
  group_posts?: {
    id: string;
    content: string;
    image_url?: string | null;
    created_at: string;
    community_id: string;
    communities?: { name: string };
    user_profiles?: { full_name: string };
  };
}

interface JoinedCommunity {
  id: string;
  name: string;
  category: string;
  member_count: number;
  is_private: boolean;
  profile_photo_url?: string | null;
  joined_at: string;
}

interface WatchlistProject {
  id: string;
  project_id: string;
  created_at: string;
  projects?: {
    id: string;
    name: string;
    category: string;
    risk_level: string;
    rating: number;
    logo_url?: string | null;
  };
}

const tagColors: Record<string, string> = {
  Earning: 'text-green-400 bg-green-400/10',
  'Lost Money': 'text-red-400 bg-red-400/10',
  Warning: 'text-yellow-400 bg-yellow-400/10',
  Question: 'text-blue-400 bg-blue-400/10',
  Discussion: 'text-muted-foreground bg-muted',
};

const riskColors: Record<string, string> = {
  Low: 'text-green-400 bg-green-400/10',
  Medium: 'text-yellow-400 bg-yellow-400/10',
  High: 'text-red-400 bg-red-400/10',
  Critical: 'text-red-500 bg-red-500/10',
  Unknown: 'text-muted-foreground bg-muted',
};

type ProfileTab = 'activity' | 'communities' | 'watchlist';

// ─── Edit Profile Modal ───────────────────────────────────────────────────────
function EditProfileModal({
  profile,
  onClose,
  onSaved,
}: {
  profile: UserProfile;
  onClose: () => void;
  onSaved: (updated: Partial<UserProfile>) => void;
}) {
  const { updateUserProfile } = useAuth();
  const [fullName, setFullName] = useState(profile.full_name || '');
  const [username, setUsername] = useState(profile.username?.replace('@', '') || '');
  const [bio, setBio] = useState(profile.bio || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!fullName.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await updateUserProfile({
        full_name: fullName.trim(),
        username: username.trim() || undefined,
        bio: bio.trim() || undefined,
      });
      onSaved({ full_name: fullName.trim(), username: username.trim() || undefined, bio: bio.trim() || undefined });
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 pb-20 sm:pb-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl max-h-[80vh] sm:max-h-none overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-base font-bold text-foreground">Edit Profile</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors">
            <XMarkIcon style={{ width: 18, height: 18 }} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-red-500/10 border border-red-500/20 rounded-xl text-xs text-red-400">
              <ExclamationCircleIcon style={{ width: 14, height: 14 }} />
              {error}
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Full Name</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your full name"
              className="w-full bg-muted border border-border rounded-xl px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/50 transition-all"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Username</label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">@</span>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value.replace(/\s/g, '').toLowerCase())}
                placeholder="username"
                className="w-full bg-muted border border-border rounded-xl pl-7 pr-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/50 transition-all"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Bio</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Tell the community about yourself..."
              rows={3}
              maxLength={200}
              className="w-full bg-muted border border-border rounded-xl px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/50 transition-all resize-none"
            />
            <p className="text-[10px] text-muted-foreground text-right">{bio.length}/200</p>
          </div>
        </div>

        <div className="flex gap-2.5 px-5 pb-5">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-60 flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(135deg, #3b6ef5 0%, #5b8af8 100%)' }}
          >
            {saving ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <CheckIcon style={{ width: 14, height: 14 }} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ProfileContent() {
  const router = useRouter();
  const { user, signOut, getUserProfile, loading, profile: contextProfile, updateUserProfile: ctxUpdateProfile, refreshProfile } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [signingOut, setSigningOut] = useState(false);
  const [activeTab, setActiveTab] = useState<ProfileTab>('activity');
  const [activitySubTab, setActivitySubTab] = useState<'posts' | 'saved'>('posts');

  // Activity
  const [myPosts, setMyPosts] = useState<ActivityPost[]>([]);
  const [savedPosts, setSavedPosts] = useState<SavedPost[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [realPostsCount, setRealPostsCount] = useState(0);
  const [savedGroupPosts, setSavedGroupPosts] = useState<SavedGroupPost[]>([]);
  const [savedSubTab, setSavedSubTab] = useState<'connect' | 'group'>('connect');

  // Communities
  const [joinedCommunities, setJoinedCommunities] = useState<JoinedCommunity[]>([]);
  const [communitiesLoading, setCommunitiesLoading] = useState(false);

  // Watchlist
  const [watchlist, setWatchlist] = useState<WatchlistProject[]>([]);
  const [watchlistLoading, setWatchlistLoading] = useState(false);

  // Avatar upload
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInputRef = useRef<HTMLInputElement>(null);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);

  // Sync profile from AuthContext (real-time updates)
  useEffect(() => {
    if (contextProfile) {
      setProfile((prev) => prev ? { ...prev, ...contextProfile } : contextProfile as UserProfile);
      setProfileLoading(false);
    }
  }, [contextProfile]);

  useEffect(() => {
    if (!loading && !user) {
      router.replace('/login');
      return;
    }
    if (user && !contextProfile) {
      getUserProfile()
        .then((data: UserProfile | null) => setProfile(data))
        .catch(() => {
          setProfile({
            full_name: user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Member',
            email: user?.email || '',
          });
        })
        .finally(() => setProfileLoading(false));
    }
  }, [user, loading, contextProfile]);

  // Fetch activity
  useEffect(() => {
    if (!user) return;
    const fetchActivity = async () => {
      setActivityLoading(true);
      const supabase = createClient();
      const { data: postsData } = await supabase
        .from('community_posts')
        .select('id, content, tag, likes, comments, created_at')
        .eq('author_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);
      if (postsData) {
        setMyPosts(postsData as ActivityPost[]);
        setRealPostsCount(postsData.length);
      }
      const { data: savedData } = await supabase
        .from('post_saves')
        .select('id, post_id, created_at, community_posts(content, tag, likes)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);
      if (savedData) {
        setSavedPosts(savedData as SavedPost[]);
        setSavedCount(savedData.length);
      }
      // Fetch saved group posts
      const { data: savedGroupData } = await supabase
        .from('group_post_saves')
        .select('id, post_id, created_at, group_posts(id, content, image_url, created_at, community_id, communities(name), user_profiles(full_name))')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);
      if (savedGroupData) {
        setSavedGroupPosts(savedGroupData as SavedGroupPost[]);
        setSavedCount((savedData?.length ?? 0) + savedGroupData.length);
      }
      setActivityLoading(false);
    };
    fetchActivity();
  }, [user]);

  // Fetch joined communities
  useEffect(() => {
    if (!user || activeTab !== 'communities') return;
    const fetchCommunities = async () => {
      setCommunitiesLoading(true);
      const supabase = createClient();
      const { data } = await supabase
        .from('community_members')
        .select('joined_at, communities(id, name, category, member_count, is_private, profile_photo_url)')
        .eq('user_id', user.id)
        .order('joined_at', { ascending: false });
      if (data) {
        const mapped = data
          .filter((row: any) => row.communities)
          .map((row: any) => ({
            id: row.communities.id,
            name: row.communities.name,
            category: row.communities.category,
            member_count: row.communities.member_count,
            is_private: row.communities.is_private,
            profile_photo_url: row.communities.profile_photo_url,
            joined_at: row.joined_at,
          }));
        setJoinedCommunities(mapped);
      }
      setCommunitiesLoading(false);
    };
    fetchCommunities();
  }, [user, activeTab]);

  // Fetch watchlist + real-time subscription
  useEffect(() => {
    if (!user || activeTab !== 'watchlist') return;
    const supabase = createClient();
    const fetchWatchlist = async () => {
      setWatchlistLoading(true);
      const { data } = await supabase
        .from('user_watchlist')
        .select('id, project_id, created_at, projects(id, name, category, risk_level, rating, logo_url)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (data) setWatchlist(data as WatchlistProject[]);
      setWatchlistLoading(false);
    };
    fetchWatchlist();

    // Real-time sync for watchlist changes
    const channel = supabase
      .channel(`watchlist_${user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_watchlist', filter: `user_id=eq.${user.id}` }, () => {
        fetchWatchlist();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, activeTab]);

  const handleCoverChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !user || coverUploading) return;
    const extensions: Record<string, string> = {
      'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/gif': 'gif',
    };
    const extension = extensions[file.type];
    if (!extension) {
      toast.error('Choose a JPG, PNG, WebP, or GIF image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Choose an image smaller than 5 MB.');
      return;
    }
    setCoverUploading(true);
    try {
      const supabase = createClient();
      const path = `covers/${user.id}/cover-${Date.now()}.${extension}`;
      const { error } = await supabase.storage.from('community-media').upload(path, file);
      if (error) throw error;
      const { data } = supabase.storage.from('community-media').getPublicUrl(path);
      await ctxUpdateProfile({ cover_url: data.publicUrl });
      setProfile((prev) => prev ? { ...prev, cover_url: data.publicUrl } : prev);
      toast.success('Wall photo updated');
    } catch {
      toast.error('Could not update your wall photo. Please try again.');
    } finally {
      setCoverUploading(false);
    }
  };

  // Avatar upload handler
  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setAvatarUploading(true);
    try {
      const supabase = createClient();
      const ext = file.name.split('.').pop();
      const path = `avatars/${user.id}/avatar-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from('community-media').upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from('community-media').getPublicUrl(path);
      const avatarUrl = urlData.publicUrl;
      // Use ctxUpdateProfile so avatar propagates app-wide
      await ctxUpdateProfile({ avatar_url: avatarUrl });
      setProfile((prev) => prev ? { ...prev, avatar_url: avatarUrl } : prev);
    } catch {
      // silently fail
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      router.push('/login');
      router.refresh();
    } catch {
      setSigningOut(false);
    }
  };

  const getInitials = (name: string) =>
    name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  const getMemberSince = (createdAt?: string) => {
    if (!createdAt) return 'Recently joined';
    const date = new Date(createdAt);
    return `Member since ${date.toLocaleString('default', { month: 'long', year: 'numeric' })}`;
  };

  const timeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading || profileLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <span className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  const displayName = profile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Member';
  const displayEmail = profile?.email || user?.email || '';
  const displayUsername = profile?.username ? `@${profile.username}` : `@${displayEmail.split('@')[0]}`;
  const totalContributions = realPostsCount + savedCount;

  const tabs: { key: ProfileTab; label: string }[] = [
    { key: 'activity', label: 'Activity' },
    { key: 'communities', label: 'Groups' },
    { key: 'watchlist', label: 'Watchlist' },
  ];

  return (
    <>
      {showEditModal && profile && (
        <EditProfileModal
          profile={profile}
          onClose={() => setShowEditModal(false)}
          onSaved={(updated) => setProfile((prev) => prev ? { ...prev, ...updated } : prev)}
        />
      )}

      <div className="space-y-5 pb-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-1">
          <h1 className="text-lg font-bold text-foreground">My Profile</h1>
          <button
            onClick={() => setShowEditModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary text-xs font-semibold transition-colors"
          >
            <PencilIcon style={{ width: 12, height: 12 }} />
            Edit
          </button>
        </div>

        {/* Profile Card */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          {/* Wall photo */}
          <div
            className="h-20 w-full relative"
            style={{ background: 'linear-gradient(135deg, #1a2a5e 0%, #0f1a3a 50%, #1a1a2e 100%)' }}
          >
            <div className="absolute inset-0 opacity-20"
              style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #3b6ef5 0%, transparent 60%), radial-gradient(circle at 80% 20%, #5b8af8 0%, transparent 50%)' }}
            />
            {profile?.cover_url && (
              <img src={profile.cover_url} alt={`${displayName} wall photo`} className="absolute inset-0 h-full w-full object-cover" />
            )}
            <button
              type="button"
              onClick={() => coverInputRef.current?.click()}
              disabled={coverUploading}
              className="absolute right-3 top-3 flex items-center gap-1.5 rounded-lg bg-black/60 px-3 py-2 text-xs font-semibold text-white hover:bg-black/75 disabled:opacity-60"
            >
              <CameraIcon className="h-4 w-4" aria-hidden="true" />
              {coverUploading ? 'Uploading...' : 'Change wall photo'}
            </button>
            <input
              ref={coverInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              aria-label="Upload wall photo"
              className="hidden"
              onChange={handleCoverChange}
              disabled={coverUploading}
            />
          </div>

          <div className="px-5 pb-5">
            {/* Avatar row */}
            <div className="flex items-end justify-between -mt-8 mb-3">
              <div className="relative">
                {profile?.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt={`${displayName} profile avatar`}
                    className="w-16 h-16 rounded-full object-cover border-4 border-card shadow-lg"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-blue-600 border-4 border-card flex items-center justify-center text-white font-bold text-xl shadow-lg">
                    {getInitials(displayName)}
                  </div>
                )}
                <button
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={avatarUploading}
                  className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-primary flex items-center justify-center shadow-md hover:bg-primary/90 transition-colors disabled:opacity-60"
                >
                  {avatarUploading ? (
                    <span className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <CameraIcon style={{ width: 11, height: 11 }} className="text-white" />
                  )}
                </button>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarChange}
                />
              </div>
              <div className="flex items-center gap-1.5 mb-1">
                <CheckBadgeIcon style={{ width: 16, height: 16 }} className="text-primary" />
                <span className="text-[10px] font-semibold text-primary">Verified Member</span>
              </div>
            </div>

            {/* Name & bio */}
            <div className="space-y-1">
              <h2 className="text-base font-bold text-foreground">{displayName}</h2>
              <p className="text-xs text-muted-foreground">{displayUsername}</p>
              <p className="text-[10px] text-muted-foreground">{getMemberSince(profile?.created_at)}</p>
              {profile?.bio ? (
                <p className="text-xs text-muted-foreground leading-relaxed pt-1">{profile.bio}</p>
              ) : (
                <button
                  onClick={() => setShowEditModal(true)}
                  className="text-xs text-primary/70 hover:text-primary transition-colors pt-1 flex items-center gap-1"
                >
                  <PencilIcon style={{ width: 10, height: 10 }} />
                  Add a bio...
                </button>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-border">
              <div className="text-center">
                <p className="text-lg font-bold text-foreground">{realPostsCount || profile?.posts_count || 0}</p>
                <p className="text-[10px] text-muted-foreground">Posts</p>
              </div>
              <div className="text-center border-x border-border">
                <p className="text-lg font-bold text-foreground">{savedCount}</p>
                <p className="text-[10px] text-muted-foreground">Saved</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-foreground">{totalContributions}</p>
                <p className="text-[10px] text-muted-foreground">Total</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contribution Banner */}
        <InviteFriends />

        <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 rounded-xl px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
            <TrophyIcon style={{ width: 18, height: 18 }} className="text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-foreground">{totalContributions} Contributions</p>
            <p className="text-[10px] text-muted-foreground">{realPostsCount} posts · {savedCount} saved</p>
          </div>
          <Link href="/community-network" className="text-xs font-semibold text-primary hover:underline flex-shrink-0">
            Post now →
          </Link>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 bg-muted p-1 rounded-xl">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${
                activeTab === tab.key
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Activity Tab ── */}
        {activeTab === 'activity' && (
          <div className="space-y-3">
            <div className="flex gap-1 bg-muted/50 p-0.5 rounded-lg w-fit">
              <button
                onClick={() => setActivitySubTab('posts')}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-all ${
                  activitySubTab === 'posts' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
                }`}
              >
                My Posts
              </button>
              <button
                onClick={() => setActivitySubTab('saved')}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-all ${
                  activitySubTab === 'saved' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
                }`}
              >
                Saved
              </button>
            </div>

            {activityLoading ? (
              <div className="flex justify-center py-6">
                <span className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              </div>
            ) : activitySubTab === 'posts' ? (
              myPosts.length === 0 ? (
                <div className="bg-card border border-border rounded-xl p-6 flex flex-col items-center gap-2 text-center">
                  <PencilSquareIcon style={{ width: 28, height: 28 }} className="text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">No posts yet</p>
                  <Link href="/community-network" className="text-xs text-primary font-semibold hover:underline">
                    Share your first post →
                  </Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {myPosts.map((post) => (
                    <div key={post.id} className="bg-card border border-border rounded-xl p-3.5 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${tagColors[post.tag] || tagColors['Discussion']}`}>
                          {post.tag}
                        </span>
                        <span className="text-[10px] text-muted-foreground">{timeAgo(post.created_at)}</span>
                      </div>
                      <p className="text-xs text-foreground leading-relaxed line-clamp-2">{post.content}</p>
                      <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <HeartIcon style={{ width: 11, height: 11 }} />
                          {post.likes}
                        </span>
                        <span className="flex items-center gap-1">
                          <ChatBubbleLeftIcon style={{ width: 11, height: 11 }} />
                          {post.comments}
                        </span>
                      </div>
                    </div>
                  ))}
                  <Link href="/community-network" className="block text-center text-xs text-primary font-semibold py-2 hover:underline">
                    View all in Community →
                  </Link>
                </div>
              )
            ) : (
              <div className="space-y-3">
                {/* Saved sub-tabs: Connect posts vs Group posts */}
                <div className="flex gap-1 bg-muted/50 p-0.5 rounded-lg w-fit">
                  <button
                    onClick={() => setSavedSubTab('connect')}
                    className={`px-3 py-1 rounded-md text-xs font-semibold transition-all ${savedSubTab === 'connect' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}
                  >
                    Connect ({savedPosts.length})
                  </button>
                  <button
                    onClick={() => setSavedSubTab('group')}
                    className={`px-3 py-1 rounded-md text-xs font-semibold transition-all ${savedSubTab === 'group' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}
                  >
                    Groups ({savedGroupPosts.length})
                  </button>
                </div>

                {savedSubTab === 'connect' ? (
                  savedPosts.length === 0 ? (
                    <div className="bg-card border border-border rounded-xl p-6 flex flex-col items-center gap-2 text-center">
                      <BookmarkIcon style={{ width: 28, height: 28 }} className="text-muted-foreground/40" />
                      <p className="text-sm text-muted-foreground">No saved posts yet</p>
                      <Link href="/community-network" className="text-xs text-primary font-semibold hover:underline">
                        Browse community →
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {savedPosts.map((saved) => {
                        const post = saved.community_posts;
                        return (
                          <div key={saved.id} className="bg-card border border-border rounded-xl p-3.5 space-y-2">
                            <div className="flex items-center justify-between gap-2">
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${tagColors[post?.tag || 'Discussion'] || tagColors['Discussion']}`}>
                                {post?.tag || 'Post'}
                              </span>
                              <span className="flex items-center gap-1 text-[10px] text-amber-400">
                                <BookmarkIcon style={{ width: 11, height: 11 }} />
                                Saved
                              </span>
                            </div>
                            <p className="text-xs text-foreground leading-relaxed line-clamp-2">{post?.content || '—'}</p>
                            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                              <HeartIcon style={{ width: 11, height: 11 }} />
                              {post?.likes ?? 0} likes
                            </div>
                          </div>
                        );
                      })}
                      <Link href="/community-network" className="block text-center text-xs text-primary font-semibold py-2 hover:underline">
                        Browse more posts →
                      </Link>
                    </div>
                  )
                ) : (
                  savedGroupPosts.length === 0 ? (
                    <div className="bg-card border border-border rounded-xl p-6 flex flex-col items-center gap-2 text-center">
                      <BookmarkIcon style={{ width: 28, height: 28 }} className="text-muted-foreground/40" />
                      <p className="text-sm text-muted-foreground">No saved group posts yet</p>
                      <Link href="/community-groups" className="text-xs text-primary font-semibold hover:underline">
                        Browse groups →
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {savedGroupPosts.map((saved) => {
                        const gp = saved.group_posts;
                        if (!gp) return null;
                        const groupName = gp.communities?.name || 'Group';
                        const authorName = gp.user_profiles?.full_name || 'Member';
                        return (
                          <Link
                            key={saved.id}
                            href={`/community-groups/${gp.community_id}`}
                            className="block bg-card border border-border rounded-xl p-3.5 space-y-2 hover:border-primary/30 transition-all"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                                {groupName}
                              </span>
                              <span className="flex items-center gap-1 text-[10px] text-amber-400">
                                <BookmarkIcon style={{ width: 11, height: 11 }} />
                                Saved
                              </span>
                            </div>
                            <p className="text-xs text-foreground leading-relaxed line-clamp-2">{gp.content || '—'}</p>
                            <p className="text-[10px] text-muted-foreground">by {authorName} · {timeAgo(gp.created_at)}</p>
                          </Link>
                        );
                      })}
                      <Link href="/community-groups" className="block text-center text-xs text-primary font-semibold py-2 hover:underline">
                        Browse more groups →
                      </Link>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Communities Tab ── */}
        {activeTab === 'communities' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs text-muted-foreground">Groups you&apos;ve joined</p>
              <Link href="/community-groups" className="text-xs text-primary font-semibold hover:underline">
                Browse all →
              </Link>
            </div>
            {communitiesLoading ? (
              <div className="flex justify-center py-6">
                <span className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              </div>
            ) : joinedCommunities.length === 0 ? (
              <div className="bg-card border border-border rounded-xl p-6 flex flex-col items-center gap-2 text-center">
                <UserGroupIcon style={{ width: 28, height: 28 }} className="text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">No groups joined yet</p>
                <Link href="/community-groups" className="text-xs text-primary font-semibold hover:underline">
                  Explore groups →
                </Link>
              </div>
            ) : (
              joinedCommunities.map((community) => (
                <Link
                  key={community.id}
                  href={`/community-groups/${community.id}`}
                  className="flex items-center gap-3 bg-card border border-border rounded-xl p-3.5 hover:border-primary/30 transition-all group"
                >
                  {community.profile_photo_url ? (
                    <img
                      src={community.profile_photo_url}
                      alt={`${community.name} group photo`}
                      className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                      <UserGroupIcon style={{ width: 18, height: 18 }} className="text-primary" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-semibold text-foreground truncate">{community.name}</p>
                      {community.is_private ? (
                        <LockClosedIcon style={{ width: 10, height: 10 }} className="text-muted-foreground flex-shrink-0" />
                      ) : (
                        <GlobeAltIcon style={{ width: 10, height: 10 }} className="text-muted-foreground flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground">{community.category} · {community.member_count} members</p>
                  </div>
                  <ChevronRightIcon style={{ width: 14, height: 14 }} className="text-muted-foreground/40 group-hover:text-primary transition-colors flex-shrink-0" />
                </Link>
              ))
            )}
          </div>
        )}

        {/* ── Watchlist Tab ── */}
        {activeTab === 'watchlist' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs text-muted-foreground">Projects you&apos;re tracking</p>
              <Link href="/project-ratings" className="text-xs text-primary font-semibold hover:underline">
                Browse projects →
              </Link>
            </div>
            {watchlistLoading ? (
              <div className="flex justify-center py-6">
                <span className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              </div>
            ) : watchlist.length === 0 ? (
              <div className="bg-card border border-border rounded-xl p-6 flex flex-col items-center gap-2 text-center">
                <EyeIcon style={{ width: 28, height: 28 }} className="text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">Your watchlist is empty</p>
                <Link href="/project-ratings" className="text-xs text-primary font-semibold hover:underline">
                  Discover projects →
                </Link>
              </div>
            ) : (
              watchlist.map((item) => {
                const project = item.projects;
                if (!project) return null;
                return (
                  <Link
                    key={item.id}
                    href={`/project/${project.id}`}
                    className="flex items-center gap-3 bg-card border border-border rounded-xl p-3.5 hover:border-primary/30 transition-all group"
                  >
                    {project.logo_url ? (
                      <img
                        src={project.logo_url}
                        alt={`${project.name} project logo`}
                        className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                        <StarIcon style={{ width: 18, height: 18 }} className="text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{project.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-muted-foreground">{project.category}</span>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${riskColors[project.risk_level] || riskColors['Unknown']}`}>
                          {project.risk_level}
                        </span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-foreground">{Number(project.rating).toFixed(1)}</p>
                      <p className="text-[10px] text-muted-foreground">rating</p>
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        )}

        {/* Badges */}
        <div>
          <h3 className="text-sm font-bold text-foreground mb-3">Badges</h3>
          <div className="grid grid-cols-4 gap-2">
            {badges.map((badge) => {
              const BadgeIcon = badge.icon;
              return (
                <div key={badge.id} className="bg-card border border-border rounded-xl p-3 flex flex-col items-center gap-2">
                  <div className={`w-10 h-10 rounded-full ${badge.bgColor} flex items-center justify-center`}>
                    <BadgeIcon style={{ width: 20, height: 20 }} className={badge.iconColor} />
                  </div>
                  <p className="text-[9px] font-medium text-muted-foreground text-center leading-tight">{badge.label}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Settings */}
        <div>
          <h3 className="text-sm font-bold text-foreground mb-3">Settings</h3>
          <div className="bg-card border border-border rounded-xl overflow-hidden divide-y divide-border">
            {[
              { id: 'set-1', label: 'Edit Profile', icon: PencilIcon, action: () => setShowEditModal(true) },
              { id: 'set-2', label: 'Notification Settings', icon: BellIcon, href: '/notifications' },
              { id: 'set-appearance', label: 'Appearance & Theme', icon: EyeIcon, href: '/settings' },
              { id: 'set-3', label: 'Privacy & Security', icon: ShieldCheckIcon, href: '#' },
              { id: 'set-4', label: 'Help & Support', icon: QuestionMarkCircleIcon, href: '#' },
            ].map((item) => {
              const ItemIcon = item.icon;
              const inner = (
                <>
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                    <ItemIcon style={{ width: 16, height: 16 }} className="text-muted-foreground" />
                  </div>
                  <span className="flex-1 text-sm font-medium text-foreground">{item.label}</span>
                  <ChevronRightIcon style={{ width: 14, height: 14 }} className="text-muted-foreground" />
                </>
              );
              return item.action ? (
                <button
                  key={item.id}
                  onClick={item.action}
                  className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-muted/50 transition-colors text-left"
                >
                  {inner}
                </button>
              ) : (
                <Link key={item.id} href={item.href!} className="flex items-center gap-3 px-4 py-3.5 hover:bg-muted/50 transition-colors">
                  {inner}
                </Link>
              );
            })}
          </div>

          <div className="pt-3">
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="w-full py-3 rounded-xl border border-red-500/30 text-red-400 text-sm font-semibold hover:bg-red-500/10 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {signingOut ? (
                <span className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
              ) : (
                <>
                  <ArrowRightOnRectangleIcon style={{ width: 16, height: 16 }} />
                  Sign Out
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
